/** The wizard model as a flow graph, and back.

    The flow designer is a second face on the SAME model the Steps tab edits:
    it holds no graph of its own. Every box is a step (plus a Start and a Finish
    terminal), and every editable arrow is one ROUTE key of a step — `next`, a
    `next` case, a branch case, `else`, a repeat's `body` / `until_step`,
    `on_saved`, `on_return.<outcome>` … — addressed by the source PORT it leaves
    from. Drawing an arrow from a port writes that key; deleting the arrow
    clears it; dragging its end re-points it. Routes that live inside
    operations, tools and panels (an op's `goto`, a tool's `goto`, a pick-row
    `then`) are drawn read-only: they are edited in the step's inspector, where
    the rest of their block is.

    Positions are presentation only (`WizardLayout`, stored beside the model by
    the server); a step with no stored position is laid out here. */

import type { WizardLayout } from '@/api/wizards'
import type { WizardCallOutcome, WizardModel, WizardNext, WizardStep, WizardStepKind } from '../types'
import { KIND_META, OUTCOMES, gotoTarget } from '../types'

export const START_ID = '__start'
export const FINISH_ID = '__finish'

// ── categories (the IOFlow node colours) ──────────────────────────────────

export type Category = 'screen' | 'system' | 'branch' | 'repeat' | 'save' | 'call' | 'start' | 'finish'

export const CATEGORY_OF: Record<WizardStepKind, Category> = {
  info: 'screen', select: 'screen', input: 'screen', scan: 'screen', grid: 'screen',
  confirm: 'screen', show: 'screen',
  fetch_url: 'system', auto: 'system', load: 'system', print: 'system', action: 'system',
  function: 'system', redirect: 'screen', sign: 'screen', send: 'system',
  branch: 'branch', repeat: 'repeat', save: 'save', call: 'call', handoff: 'call',
}

/** Tone per category — IOFlow's palette: action green, condition yellow, loop
 *  purple, manual trigger orange, response red, plus blue / indigo. */
export const CATEGORY_TONE: Record<Category, string> = {
  screen: '#3b82f6',
  system: '#22c55e',
  branch: '#f59e0b',
  repeat: '#a855f7',
  save: '#ef4444',
  call: '#6366f1',
  start: '#f97316',
  finish: '#ec4899',
}

export const CATEGORY_LABEL: Record<Category, string> = {
  screen: 'Screens',
  system: 'Runs on its own',
  branch: 'Flow control',
  repeat: 'Flow control',
  save: 'Save',
  call: 'Other wizards',
  start: 'Start',
  finish: 'Finish',
}

// ── ports ─────────────────────────────────────────────────────────────────

export type PortTone = 'next' | 'case' | 'else' | 'body' | 'return' | 'issue' | 'saved'

export interface Port {
  /** The source handle id — also the route key it writes. */
  id: string
  label: string
  tone: PortTone
  /** Accepts the Finish terminal as a target. */
  finish?: boolean
  /** A port that ADDS a route rather than naming an existing one. */
  adds?: boolean
}

const OUTCOME_LABEL: Record<WizardCallOutcome, string> = {
  completed: 'completed',
  cancelled: 'cancelled',
  pending_approval: 'pending approval',
  pending_assignee: 'pending assignee',
  failed: 'failed',
}

type NextCase = { when?: string; goto?: string; finish?: true }

function nextCases(next: WizardNext | undefined): NextCase[] | null {
  return Array.isArray(next) ? (next as NextCase[]) : null
}

/** The side ports of a step — every route that is not its plain `next`. The
 *  bottom `next` handle is separate (every step but a branch has one). */
export function portsOf(step: WizardStep): Port[] {
  const out: Port[] = []
  const cases = nextCases(step.next)
  cases?.forEach((c, i) => out.push({
    id: `next:${i}`, label: c.when ? `if ${c.when}` : 'otherwise', tone: c.when ? 'case' : 'next', finish: true,
  }))
  switch (step.kind) {
    case 'branch':
      (step.cases || []).forEach((c, i) => out.push({ id: `case:${i}`, label: c.when || '(no condition)', tone: 'case' }))
      out.push({ id: 'else', label: 'else', tone: 'else' })
      out.push({ id: 'case:new', label: '+ case', tone: 'case', adds: true })
      break
    case 'repeat':
      out.push({ id: 'body', label: 'per line', tone: 'body' })
      out.push({ id: 'until', label: 'until', tone: 'body' })
      break
    case 'save':
      out.push({ id: 'on_saved', label: 'saved', tone: 'saved' })
      out.push({ id: 'on_pending_approval', label: 'pending approval', tone: 'case' })
      break
    case 'load':
      out.push({ id: 'found', label: 'found', tone: 'case' })
      out.push({ id: 'missing', label: 'missing', tone: 'case' })
      break
    case 'call':
      for (const o of OUTCOMES) out.push({ id: `return:${o}`, label: `on ${OUTCOME_LABEL[o]}`, tone: o === 'failed' ? 'issue' : 'return' })
      break
    default:
      break
  }
  if (step.kind === 'fetch_url' || step.kind === 'function' || step.kind === 'redirect' || gotoTarget(step.on_error)) {
    out.push({ id: 'on_error', label: 'on error', tone: 'issue' })
  }
  ;(step.on_issue || []).forEach((iss, i) => out.push({ id: `issue:${i}`, label: `issue ${iss.code || i + 1}`, tone: 'issue' }))
  return out
}

/** A step with no bottom `next` handle. */
export function hasNextHandle(step: WizardStep): boolean {
  return step.kind !== 'branch' && step.kind !== 'handoff'
}

// ── edges ─────────────────────────────────────────────────────────────────

export interface RouteEdge {
  id: string
  from: string
  /** Source handle id: `next`, a port id, `start`, or `aux` (read-only). */
  port: string
  to: string
  label?: string
  tone: PortTone | 'readonly' | 'implicit'
  /** Not a route key — drawn, but edited in the inspector. */
  readonly?: boolean
  /** "The following step in the list": no `next` written. */
  implicit?: boolean
}

function edge(from: string, port: string, to: string, tone: RouteEdge['tone'], extra: Partial<RouteEdge> = {}): RouteEdge {
  return { id: `${from}|${port}|${to}|${extra.label || ''}`, from, port, to, tone, ...extra }
}

function auxEdges(s: WizardStep, out: RouteEdge[]) {
  const ro = (to: string | null | undefined, label: string) => {
    if (to) out.push(edge(s.id, 'aux', to, 'readonly', { label, readonly: true }))
  }
  for (const op of [...(s.then || []), ...(s.do || [])]) {
    if (op.op === 'goto') ro(op.goto, 'op: goto')
    ro(gotoTarget(op.on_none), 'op: on none')
    ro(gotoTarget(op.on_many), 'op: on many')
  }
  for (const t of [...(s.tools || []), ...(s.row_tools || [])]) if (t.kind === 'goto') ro(t.goto, `tool: ${t.label || 'goto'}`)
  for (const p of [...(s.context || []), ...(s.content ? [s.content] : [])]) ro(gotoTarget(p.pick_row?.then), 'pick row')
}

/** Every edge the canvas draws, editable routes first. */
export function routeEdges(model: WizardModel): RouteEdge[] {
  const out: RouteEdge[] = []
  const ids = new Set(model.steps.map((s) => s.id))
  if (model.start && ids.has(model.start)) out.push(edge(START_ID, 'start', model.start, 'next'))
  model.steps.forEach((s, i) => {
    const next = s.next
    if (typeof next === 'string' && next) out.push(edge(s.id, 'next', next, 'next'))
    else if (next && !Array.isArray(next) && typeof next === 'object' && 'finish' in next) out.push(edge(s.id, 'next', FINISH_ID, 'next'))
    else if (Array.isArray(next)) {
      ;(next as NextCase[]).forEach((c, j) => {
        const to = c.finish ? FINISH_ID : c.goto
        if (to) out.push(edge(s.id, `next:${j}`, to, c.when ? 'case' : 'next'))
      })
    } else if (next === undefined && hasNextHandle(s) && model.steps[i + 1]) {
      out.push(edge(s.id, 'next', model.steps[i + 1].id, 'implicit', { implicit: true, label: '(following)' }))
    }
    ;(s.cases || []).forEach((c, j) => { if (c.goto) out.push(edge(s.id, `case:${j}`, c.goto, 'case')) })
    if (s.else) out.push(edge(s.id, 'else', s.else, 'else'))
    if (s.kind === 'repeat' && typeof s.body === 'string' && s.body) out.push(edge(s.id, 'body', s.body, 'body'))
    if (s.until_step) out.push(edge(s.id, 'until', s.until_step, 'body'))
    if (s.on_saved) out.push(edge(s.id, 'on_saved', s.on_saved, 'saved'))
    if (s.on_pending_approval) out.push(edge(s.id, 'on_pending_approval', s.on_pending_approval, 'case'))
    const err = gotoTarget(s.on_error)
    if (err) out.push(edge(s.id, 'on_error', err, 'issue'))
    if (s.on_found?.goto) out.push(edge(s.id, 'found', s.on_found.goto, 'case'))
    const miss = s.on_missing
    const missTo = (miss && typeof miss === 'object' ? miss.goto : null) || gotoTarget(miss)
    if (missTo) out.push(edge(s.id, 'missing', missTo, 'case'))
    if (s.kind === 'call') {
      for (const [o, r] of Object.entries(s.on_return || {})) {
        if (!r) continue
        const to = r.goto || (r.resume && !['same', 'next'].includes(r.resume) ? r.resume : null)
        if (to) out.push(edge(s.id, `return:${o}`, to, o === 'failed' ? 'issue' : 'return'))
      }
    }
    ;(s.on_issue || []).forEach((iss, j) => { if (iss.goto) out.push(edge(s.id, `issue:${j}`, iss.goto, 'issue')) })
    auxEdges(s, out)
  })
  const known = new Set([...ids, FINISH_ID])
  // De-duplicate (two read-only routes to the same step with the same label).
  const seen = new Set<string>()
  return out.filter((e) => known.has(e.to) && !seen.has(e.id) && !!seen.add(e.id))
}

// ── writing routes ────────────────────────────────────────────────────────

function cleaned<T extends object>(o: T): T {
  const r = { ...o } as Record<string, unknown>
  for (const k of Object.keys(r)) if (r[k] === undefined) delete r[k]
  return r as T
}

function patchStep(model: WizardModel, id: string, fn: (s: WizardStep) => WizardStep): WizardModel {
  return { ...model, steps: model.steps.map((s) => (s.id === id ? cleaned(fn(s)) : s)) }
}

/** Whether `port` of a step may point at the Finish terminal. */
export function portTakesFinish(port: string): boolean {
  return port === 'next' || /^next:\d+$/.test(port)
}

/** Point the route `port` of step `from` at `to` (a step id or FINISH_ID). */
export function connectRoute(model: WizardModel, from: string, port: string, to: string): WizardModel {
  if (from === START_ID) return port === 'start' && to !== FINISH_ID ? { ...model, start: to } : model
  if (to === FINISH_ID && !portTakesFinish(port)) return model
  const route = (c: NextCase): NextCase => (to === FINISH_ID ? { when: c.when, finish: true } : { when: c.when, goto: to })
  return patchStep(model, from, (s) => {
    const idx = Number(port.split(':')[1])
    if (port === 'next') {
      const cases = nextCases(s.next)
      if (!cases) return { ...s, next: (to === FINISH_ID ? { finish: true } : to) as WizardNext }
      // Ordered cases: the arrow becomes the default (a case without `when`),
      // replacing the default that is there.
      const kept = cases.filter((c) => c.when)
      return { ...s, next: [...kept, route({})].map(cleaned) as WizardNext }
    }
    if (port.startsWith('next:')) {
      const cases = [...(nextCases(s.next) || [])]
      if (!cases[idx]) return s
      cases[idx] = cleaned(route(cases[idx]))
      return { ...s, next: cases as WizardNext }
    }
    if (port === 'case:new') return { ...s, cases: [...(s.cases || []), { when: '', goto: to }] }
    if (port.startsWith('case:')) {
      const cases = [...(s.cases || [])]
      if (!cases[idx]) return s
      cases[idx] = { ...cases[idx], goto: to }
      return { ...s, cases }
    }
    if (port.startsWith('issue:')) {
      const list = [...(s.on_issue || [])]
      if (!list[idx]) return s
      list[idx] = { ...list[idx], goto: to }
      return { ...s, on_issue: list }
    }
    if (port.startsWith('return:')) {
      const o = port.slice('return:'.length) as WizardCallOutcome
      const prev = { ...(s.on_return?.[o] || {}) }
      delete prev.resume
      return { ...s, on_return: { ...(s.on_return || {}), [o]: { ...prev, goto: to } } }
    }
    switch (port) {
      case 'else': return { ...s, else: to }
      case 'body': return { ...s, body: to }
      case 'until': return { ...s, until_step: to }
      case 'on_saved': return { ...s, on_saved: to }
      case 'on_pending_approval': return { ...s, on_pending_approval: to }
      case 'on_error': return { ...s, on_error: `goto:${to}` }
      case 'found': return { ...s, on_found: { ...(s.on_found || {}), goto: to } }
      case 'missing': {
        const m = s.on_missing && typeof s.on_missing === 'object' ? s.on_missing : {}
        return { ...s, on_missing: { ...m, goto: to } }
      }
      default: return s
    }
  })
}

/** Clear the route `port` of step `from` (the arrow was deleted). */
export function disconnectRoute(model: WizardModel, from: string, port: string): WizardModel {
  if (from === START_ID) return model
  return patchStep(model, from, (s) => {
    const idx = Number(port.split(':')[1])
    if (port === 'next') {
      const cases = nextCases(s.next)
      if (cases) {
        const kept = cases.filter((c) => c.when)
        return { ...s, next: kept.length ? (kept as WizardNext) : undefined }
      }
      return { ...s, next: undefined }
    }
    if (port.startsWith('next:')) {
      const cases = (nextCases(s.next) || []).filter((_, j) => j !== idx)
      return { ...s, next: cases.length ? (cases as WizardNext) : undefined }
    }
    if (port.startsWith('case:')) return { ...s, cases: (s.cases || []).filter((_, j) => j !== idx) }
    if (port.startsWith('issue:')) {
      return { ...s, on_issue: (s.on_issue || []).map((x, j) => (j === idx ? cleaned({ ...x, goto: undefined }) : x)) }
    }
    if (port.startsWith('return:')) {
      const o = port.slice('return:'.length) as WizardCallOutcome
      const rest = { ...(s.on_return || {}) }
      const r = { ...(rest[o] || {}) }
      delete r.goto
      if (r.resume && !['same', 'next'].includes(r.resume)) delete r.resume
      if (Object.keys(r).length) rest[o] = r
      else delete rest[o]
      return { ...s, on_return: rest }
    }
    switch (port) {
      case 'else': return { ...s, else: undefined }
      case 'body': return { ...s, body: undefined }
      case 'until': return { ...s, until_step: undefined }
      case 'on_saved': return { ...s, on_saved: undefined }
      case 'on_pending_approval': return { ...s, on_pending_approval: undefined }
      case 'on_error': return { ...s, on_error: undefined }
      case 'found': {
        const f = cleaned({ ...(s.on_found || {}), goto: undefined })
        return { ...s, on_found: Object.keys(f).length ? f : undefined }
      }
      case 'missing': {
        if (s.on_missing && typeof s.on_missing === 'object') {
          const m = cleaned({ ...s.on_missing, goto: undefined })
          return { ...s, on_missing: Object.keys(m).length ? m : undefined }
        }
        return { ...s, on_missing: undefined }
      }
      default: return s
    }
  })
}

/** Remove a step and every editable route that pointed at it. `start` moves to
 *  the first remaining step when it was the one removed. */
export function removeStep(model: WizardModel, id: string): WizardModel {
  let m = model
  for (const e of routeEdges(model)) {
    if (e.to === id && !e.readonly && !e.implicit && e.from !== START_ID) m = disconnectRoute(m, e.from, e.port)
  }
  const steps = m.steps.filter((s) => s.id !== id)
  return { ...m, steps, start: m.start === id ? (steps[0]?.id || '') : m.start }
}

// ── summaries (the second line of a box) ──────────────────────────────────

export function stepSummary(s: WizardStep): string {
  switch (s.kind) {
    case 'select': return s.target ? `→ ${s.target}` : 'pick a value'
    case 'input': return `${(s.fields || []).length} field${(s.fields || []).length === 1 ? '' : 's'}`
    case 'scan': return s.parse?.format ? `parse: ${s.parse.format}` : 'scan'
    case 'grid': return `${(s.columns || []).length} column${(s.columns || []).length === 1 ? '' : 's'}`
    case 'confirm': return s.validate ? 'validate, then confirm' : 'confirm'
    case 'show': return s.content?.obj_name ? `${s.content.kind}: ${s.content.obj_name}` : 'show an object'
    case 'fetch_url': return s.request?.connection || s.request?.path || s.request?.url || 'read from a URL'
    case 'auto': return `${(s.do || []).length} operation${(s.do || []).length === 1 ? '' : 's'}`
    case 'branch': return `${(s.cases || []).length} case${(s.cases || []).length === 1 ? '' : 's'}`
    case 'load': return `match ${Object.keys(s.match || {}).join(', ') || '—'}`
    case 'repeat': return s.over ? `over ${s.over}` : 'per line'
    case 'save': return `warnings: ${s.warnings || 'ask'}`
    case 'print': return s.file?.repository
      ? `${s.report?.obj_name || 'a report'} → ${s.file.repository}`
      : s.report?.obj_name || 'print a report'
    case 'sign': return s.document ? `sign the filing of ${s.document}` : 'sign a filed report'
    case 'send': return s.action ? `send ${s.action}` : 'send a message'
    case 'action': return s.action || 'run an action'
    case 'call': return s.wizard ? `call ${s.wizard}` : 'call a wizard'
    case 'handoff': return s.wizard ? `hand off to ${s.wizard}` : 'hand off'
    case 'info': return s.acknowledge ? 'acknowledge' : 'information'
    default: return String((s as WizardStep).kind)
  }
}

// ── layout ────────────────────────────────────────────────────────────────

export const NODE_W = 240
const HEAD_H = 74
const PORT_H = 22
const GAP_X = 70
const GAP_Y = 80

export function nodeHeight(step: WizardStep | null): number {
  return HEAD_H + (step ? portsOf(step).length * PORT_H : 0)
}

export interface Pos { x: number; y: number }

/** Top-down layers by distance from Start (IOFlow draws its flows top to
 *  bottom), each layer centred. Steps no route reaches go last. */
export function autoLayout(model: WizardModel): Record<string, Pos> {
  const edges = routeEdges(model).filter((e) => e.from !== e.to)
  const ids = [START_ID, ...model.steps.map((s) => s.id), FINISH_ID]
  const byId = new Map(model.steps.map((s) => [s.id, s]))
  const layer = new Map<string, number>([[START_ID, 0]])
  const queue = [START_ID]
  while (queue.length) {
    const cur = queue.shift()!
    for (const e of edges) {
      if (e.from !== cur || layer.has(e.to) || e.to === FINISH_ID) continue
      layer.set(e.to, (layer.get(cur) || 0) + 1)
      queue.push(e.to)
    }
  }
  const maxReached = Math.max(0, ...layer.values())
  let spill = maxReached + 1
  for (const id of ids) {
    if (id === FINISH_ID || layer.has(id)) continue
    layer.set(id, spill++)
  }
  layer.set(FINISH_ID, Math.max(0, ...layer.values()) + 1)
  const rows = new Map<number, string[]>()
  for (const id of ids) {
    const l = layer.get(id) || 0
    if (!rows.has(l)) rows.set(l, [])
    rows.get(l)!.push(id)
  }
  const widest = Math.max(1, ...[...rows.values()].map((r) => r.length))
  const fullW = widest * NODE_W + (widest - 1) * GAP_X
  const out: Record<string, Pos> = {}
  let y = 0
  for (const l of [...rows.keys()].sort((a, b) => a - b)) {
    const row = rows.get(l)!
    const rowW = row.length * NODE_W + (row.length - 1) * GAP_X
    const x0 = (fullW - rowW) / 2
    let tallest = 0
    row.forEach((id, i) => {
      out[id] = { x: Math.round(x0 + i * (NODE_W + GAP_X)), y }
      const step = byId.get(id) || null
      tallest = Math.max(tallest, id === START_ID || id === FINISH_ID ? 70 : nodeHeight(step))
    })
    y += tallest + GAP_Y
  }
  return out
}

/** Stored positions where there are some; the automatic layout for the rest.
 *  A new step lands below its first predecessor rather than in the spill row. */
export function positions(model: WizardModel, layout: WizardLayout): Record<string, Pos> {
  const stored = layout.nodes || {}
  const ids = [START_ID, ...model.steps.map((s) => s.id), FINISH_ID]
  if (!ids.some((id) => stored[id])) return autoLayout(model)
  const auto = autoLayout(model)
  const out: Record<string, Pos> = {}
  for (const id of ids) if (stored[id]) out[id] = { x: stored[id].x, y: stored[id].y }
  const edges = routeEdges(model)
  for (const id of ids) {
    if (out[id]) continue
    const pred = edges.find((e) => e.to === id && out[e.from])
    out[id] = pred
      ? { x: out[pred.from].x, y: out[pred.from].y + nodeHeight(model.steps.find((s) => s.id === pred.from) || null) + GAP_Y }
      : auto[id]
  }
  return out
}

/** The layout after a rename: the box keeps its place. */
export function renameInLayout(layout: WizardLayout, from: string, to: string): WizardLayout {
  if (!layout.nodes?.[from]) return layout
  const nodes = { ...layout.nodes }
  nodes[to] = nodes[from]
  delete nodes[from]
  return { ...layout, nodes }
}
